import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  effect,
  input,
} from '@angular/core';

import { FormFieldControlAdapter } from './form-field-control-adapter';

@Component({
  selector: 'app-form-field',
  standalone: true,
  templateUrl: './form-field.component.html',
  styleUrl: './form-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'form-field',
    '[class.form-field--invalid]': 'invalid()',
    '[class.form-field--disabled]': 'disabled()',
    '[class.form-field--required]': 'required()',
  },
})
export class FormFieldComponent implements AfterContentInit {
  private static nextFieldId = 0;
  private readonly fallbackFieldId = `form-field-${FormFieldComponent.nextFieldId++}`;

  readonly control = contentChild(FormFieldControlAdapter, { descendants: true });

  readonly label = input.required<string>();
  readonly required = input(false);
  readonly hint = input<string | null>(null);
  readonly error = input<string | null>(null);
  readonly disabled = input<boolean | null>(null);
  readonly invalid = input(false);

  readonly fieldId = computed(() => this.control()?.id() ?? this.fallbackFieldId);
  readonly labelId = computed(() => `${this.fieldId()}-label`);
  readonly labelForId = computed(() => this.control()?.labelForId() ?? null);
  readonly hintId = computed(() => (this.hint() ? `${this.fieldId()}-hint` : null));
  readonly errorId = computed(() => (this.error() ? `${this.fieldId()}-error` : null));
  readonly describedByIds = computed(() => {
    const ids = [this.hintId(), this.errorId()].filter((value): value is string => value !== null);
    return ids;
  });

  constructor() {
    effect(() => {
      const control = this.control();
      if (!control) {
        return;
      }

      control.setLabelledByIds([this.labelId()]);
      control.setDescribedByIds(this.describedByIds());
      control.setInvalid(this.invalid());
      control.setRequired(this.required());
      control.setDisabled(this.disabled());
    });
  }

  ngAfterContentInit(): void {
    if (!this.control()) {
      throw new Error('FormFieldComponent requires exactly one projected control marked with [formFieldControl].');
    }
  }

  onLabelClick(event: MouseEvent): void {
    const control = this.control();
    if (!control || control.labelForId() !== null) {
      return;
    }

    control.focus?.();
    event.preventDefault();
  }
}
